// 계약갱신관리 — 목록 불러오기·갱신/종료·비고·되돌리기·직접 등록 삭제·설정 창 열기 (상태와 저장)
// 2026-11-04 ContractRenewalManagement.jsx(374줄)에서 글자 그대로 옮겼다(300줄 규칙, 동작 변화 없음). 화면 그리기는 그 파일에 남는다.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';

export function normalizeCompanyCode(value) {
  return String(value || 'PINE').trim().toUpperCase() || 'PINE';
}

function defaultApiBase(companyCode, integratedMenuId) {
  if (integratedMenuId) return `/api/admin/integrated-menus/${integratedMenuId}/contract-renewals`;
  const code = normalizeCompanyCode(companyCode);
  return code === 'PINE'
    ? '/api/admin/employee-contract-renewals'
    : `/api/admin/ext/${code}/employee-contract-renewals`;
}

function periodLabel(value, unit) {
  const amount = Number(value || 0);
  if (amount <= 0) return '3개월';
  const label = unit === 'year' ? '년' : unit === 'day' ? '일' : '개월';
  return `${amount}${label}`;
}

export function useContractRenewal({
  companyCode,
  apiBase,
  integratedMenuId,
  companyCodes,
  selectedCompanyCodes,
}) {
  const normalizedCompanyCode = useMemo(() => normalizeCompanyCode(companyCode), [companyCode]);
  const normalizedCompanyCodes = useMemo(
    () => companyCodes.map(normalizeCompanyCode).filter(Boolean),
    [companyCodes]
  );
  const activeCompanyCodes = useMemo(() => {
    if (!integratedMenuId) return [normalizedCompanyCode];
    const selected = Array.isArray(selectedCompanyCodes) && selectedCompanyCodes.length > 0
      ? selectedCompanyCodes.map(normalizeCompanyCode).filter((code) => normalizedCompanyCodes.includes(code))
      : normalizedCompanyCodes;
    return selected.length > 0 ? selected : normalizedCompanyCodes;
  }, [integratedMenuId, normalizedCompanyCode, normalizedCompanyCodes, selectedCompanyCodes]);
  const resolvedApiBase = useMemo(
    () => apiBase || defaultApiBase(normalizedCompanyCode, integratedMenuId),
    [apiBase, integratedMenuId, normalizedCompanyCode]
  );
  const canManageSettings = true;
  const defaultSetupCompanyCode = activeCompanyCodes[0] || normalizedCompanyCode;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [includeResigned, setIncludeResigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState('');
  const [detail, setDetail] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [setupCompanyCode, setSetupCompanyCode] = useState(defaultSetupCompanyCode);
  const [memoDrafts, setMemoDrafts] = useState({});

  useBodyScrollLock(canManageSettings && (manualOpen || settingsOpen));

  useEffect(() => {
    setSetupCompanyCode((prev) => (
      activeCompanyCodes.includes(prev) ? prev : defaultSetupCompanyCode
    ));
  }, [activeCompanyCodes, defaultSetupCompanyCode]);

  const query = useMemo(() => {
    const params = new URLSearchParams({ status, include_resigned: String(includeResigned) });
    if (search.trim()) params.set('q', search.trim());
    if (integratedMenuId && activeCompanyCodes.length > 0) params.set('company_codes', activeCompanyCodes.join(','));
    return params.toString();
  }, [activeCompanyCodes, includeResigned, integratedMenuId, search, status]);

  const endpoint = useCallback((path = '', extra = {}) => {
    const params = new URLSearchParams(extra);
    const suffix = params.toString();
    return `${resolvedApiBase}${path}${suffix ? `?${suffix}` : ''}`;
  }, [resolvedApiBase]);

  const bodyWithCompany = useCallback((body = {}, code = normalizedCompanyCode) => {
    if (!integratedMenuId) return body;
    return { ...body, company_code: normalizeCompanyCode(code) };
  }, [integratedMenuId, normalizedCompanyCode]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setMessage('');
    const res = await apiClient.get(`${resolvedApiBase}${query ? `?${query}` : ''}`);
    setLoading(false);
    if (!res.ok || !res.data?.success) {
      setRows([]);
      setSummary({});
      setMessage(res.data?.message || '계약갱신관리 목록을 불러오지 못했습니다.');
      return;
    }
    setRows(res.data.data?.rows || []);
    setSummary(res.data.data?.summary || {});
  }, [query, resolvedApiBase]);

  useEffect(() => { loadRows(); }, [loadRows]);

  async function openDetail(row) {
    const res = await apiClient.get(endpoint(`/${row.user_id}`, integratedMenuId ? { company_code: row.company_code } : {}));
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '상세 이력을 불러오지 못했습니다.');
      return;
    }
    setDetail(res.data.data);
    setMemoDrafts({});
  }

  async function reloadDetail(userId, code = detail?.user?.company_code || normalizedCompanyCode) {
    if (!userId) return;
    const res = await apiClient.get(endpoint(`/${userId}`, integratedMenuId ? { company_code: code } : {}));
    if (res.ok && res.data?.success) setDetail(res.data.data);
  }

  async function runAction(row, action) {
    const isRenew = action === 'renew';
    const renewalLabel = periodLabel(row.enrollment?.renewal_period_value || 3, row.enrollment?.renewal_period_unit || 'month');
    const text = isRenew
      ? `${row.name}님의 현재 계약을 갱신하고 다음 ${renewalLabel} 계약 구간을 만들까요?`
      : `${row.name}님의 현재 계약을 종료로 표시할까요?\n직원관리 퇴사일은 자동으로 바뀌지 않습니다.`;
    if (!window.confirm(text)) return;
    setSavingId(row.user_id);
    setMessage('');
    const res = await apiClient.post(endpoint(`/${row.user_id}/${action}`), bodyWithCompany({}, row.company_code));
    setSavingId(null);
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '저장에 실패했습니다.');
      return;
    }
    setMessage(isRenew ? '계약 갱신을 저장했습니다.' : '계약 종료를 저장했습니다.');
    await loadRows();
    await reloadDetail(row.user_id, row.company_code);
  }

  async function saveMemo(termId) {
    setSavingId(termId);
    const res = await apiClient.patch(endpoint(`/terms/${termId}`), bodyWithCompany({
      memo: memoDrafts[termId] ?? '',
    }, detail?.user?.company_code));
    setSavingId(null);
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '비고 저장에 실패했습니다.');
      return;
    }
    setMessage('비고를 저장했습니다.');
    setDetail(res.data.data);
    setMemoDrafts({});
    await loadRows();
  }

  async function undoLast(user) {
    if (!user?.user_id) return;
    if (!window.confirm(`${user.name}님의 최근 갱신/종료 작업을 되돌릴까요?`)) return;
    setSavingId(`undo-${user.user_id}`);
    setMessage('');
    const res = await apiClient.post(endpoint(`/${user.user_id}/undo-last`), bodyWithCompany({}, user.company_code));
    setSavingId(null);
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '최근 작업 되돌리기에 실패했습니다.');
      return;
    }
    setMessage('최근 작업을 되돌렸습니다.');
    setDetail(res.data.data);
    setMemoDrafts({});
    await loadRows();
  }

  async function deleteManual(row) {
    if (!window.confirm(`${row.name}님을 계약갱신관리 대상에서 삭제할까요?\n직접 등록으로 만든 미결정 구간도 함께 삭제됩니다.`)) return;
    setSavingId(row.user_id);
    setMessage('');
    const res = await apiClient.delete(endpoint(`/enrollments/${row.user_id}`, integratedMenuId ? { company_code: row.company_code } : {}));
    setSavingId(null);
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '직접 등록 삭제에 실패했습니다.');
      return;
    }
    setMessage(`${row.name}님 직접 등록을 삭제했습니다.`);
    if (detail?.user?.user_id === row.user_id) setDetail(null);
    await loadRows();
  }

  const handlers = {
    onDetail: openDetail,
    onRenew: (row) => runAction(row, 'renew'),
    onEnd: (row) => runAction(row, 'end'),
    onDelete: deleteManual,
  };

  const setupCompanyOptions = integratedMenuId ? normalizedCompanyCodes : [];
  const settingsCompanyCode = integratedMenuId ? setupCompanyCode : '';
  const calendarCompanyCode = integratedMenuId ? defaultSetupCompanyCode : normalizedCompanyCode;

  function openManualSettings() {
    setSetupCompanyCode(defaultSetupCompanyCode);
    setManualOpen(true);
  }

  function openDepartmentSettings() {
    setSetupCompanyCode(defaultSetupCompanyCode);
    setSettingsOpen(true);
  }

  return {
    normalizedCompanyCodes, activeCompanyCodes, resolvedApiBase, canManageSettings, rows, summary,
    status, setStatus, search, setSearch, includeResigned, setIncludeResigned,
    loading, savingId, message, setMessage, detail, setDetail,
    settingsOpen, setSettingsOpen, manualOpen, setManualOpen, setupCompanyCode, setSetupCompanyCode,
    memoDrafts, setMemoDrafts, loadRows, openDetail, saveMemo, undoLast,
    runAction, handlers, setupCompanyOptions, settingsCompanyCode, calendarCompanyCode, openManualSettings,
    openDepartmentSettings,
  };
}
